import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define types for request and response
export interface UserCreateInput {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  employee_id: string;
  role: string;
  contact_number: string;
  reporting_manager: string;
  department: string;
  user_locations: string;
  language: string;
}

export interface UserInviteInput {
  first_name: string;
  last_name: string;
  email: string;
  employee_id: string;
  role: string;
  phone_number: string;
  reporting_manager: string;
  department: string;
  location: string;
  // language: string;
}

export interface User {
  [x: string]: any;
  company_details: {
    id: string;
    name: string;
    description: string;
    profile: {
      logo: string;
      logo_url: string;
    }
  };
  roles: Array<{
    id: string;
    name: string;
    description: string;
    role_type: string;
    is_system_role: boolean;
    company: string;
    company_name: string;
  }>;
  permissions: Array<{
    role_id: string;
    role_name: string;
    permission_id: string;
    permission_name: string;
    permission_description: string;
    company_id: string;
  }>;
  additional_info: {
    profile_picture: string | null;
    bio: string;
    date_of_birth: string;
    address: string;
  };
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  auth0_user_id: string | null;
  auth0_invitation_id: string | null;
  invitation_sent_at: string | null;
  invitation_accepted_at: string | null;
  role: string;
  department?: string;
  position?: string;
  employee_id?: string;
  phone_number?: string;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

export interface UserFilterParams {
  page?: number;
  page_size?: number;
  invitation_status?: 'sent' | 'accepted' | 'all';
  search?: string;
  role?: string;
  department?: string;
}

export interface Auth0MeResponse {
  user: User;
  access_token: string;
  expires_in: number;
  expires_at: number;
  issued_at: number;
  token_type: string;
  company_details?: {
    id: string;
    name: string;
    description: string;
    profile?: {
      logo?: string;
    };
  };
}

// Create API
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    listUsers: builder.query<UserListResponse, UserFilterParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page.toString());
        if (params.page_size)
          queryParams.append('page_size', params.page_size.toString());
        if (params.invitation_status && params.invitation_status !== 'all') {
          queryParams.append('invitation_status', params.invitation_status);
        }
        if (params.search) queryParams.append('search', params.search);
        if (params.role) queryParams.append('role', params.role);
        if (params.department)
          queryParams.append('department', params.department);

        queryParams.append('is_active', 'all'); // Always list all users

        const queryString = queryParams.toString();
        return {
          url: `users/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
    }),

    getUserById: builder.query<User, string>({
      query: (userId) => ({
        url: `users/${userId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    inviteUser: builder.mutation<User, UserInviteInput>({
      query: (userData) => ({
        url: 'users/auth0/invite/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    createUser: builder.mutation<User, UserCreateInput>({
      query: (userData) => ({
        url: 'users/auth0/invite/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    listAllUsers: builder.query<UserListResponse, void>({
      query: () => ({
        url: 'users/',
        method: 'GET',
        params: { page_size: 25 }, // Fetch all users with a large page size
      }),
    }),

    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: 'me/',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    updateCurrentUser: builder.mutation<User, Partial<User>>({
      query: (userData) => ({
        url: 'me/',
        method: 'PATCH',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    // New endpoint for auth0/me
    getAuth0Me: builder.query<Auth0MeResponse, void>({
      query: () => ({
        url: 'auth0/me/',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    resendInvitation: builder.mutation<User, string>({
      query: (userId) => ({
        url: `users/${userId}/resend-invitation/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [
        { type: 'User', id: userId },
      ],
    }),
    updateUser: builder.mutation<
      User,
      { userId: string; userData: Partial<User> }
    >({
      query: ({ userId, userData }) => ({
        url: `users/${userId}/`,
        method: 'PATCH',
        body: userData,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
      ],
    }),
  }),
});

// Export hooks
export const {
  useListUsersQuery,
  useGetUserByIdQuery,
  useInviteUserMutation,
  useCreateUserMutation,
  useResendInvitationMutation,
  useListAllUsersQuery,
  useUpdateUserMutation,
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
  useGetAuth0MeQuery, // Export the new hook
} = userApi;
